import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Save,
  Send,
  DollarSign,
  Package,
  User,
  Calendar,
  FileText,
  Archive
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { showNotification, showError } from '../../utils/notificationHandler';
import PageHeader from '../shared/PageHeader';
import Button from '../shared/Button';

const InquiryReview = ({ context = 'staff' }) => {
  const { id } = useParams();
  const history = useHistory();
  const [inquiry, setInquiry] = useState(null);
  const [items, setItems] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [staffNotes, setStaffNotes] = useState('');
  const [quoteReference, setQuoteReference] = useState('');

  // Fetch inquiry details
  useEffect(() => {
    fetchInquiryDetails();
  }, [id]);

  const fetchInquiryDetails = async () => {
    try {
      setLoading(true);

      // Fetch inquiry list
      const { data: inquiryData, error: inquiryError } = await supabase
        .from('inquiry_lists')
        .select('*')
        .eq('id', id)
        .single();

      if (inquiryError) throw inquiryError;
      setInquiry(inquiryData);
      setStaffNotes(inquiryData.notes || '');
      setQuoteReference(inquiryData.quote_reference || '');

      // Fetch customer details
      const { data: customerData, error: customerError } = await supabase
        .from('accounts')
        .select('*')
        .eq('id', inquiryData.user_id)
        .single();

      if (customerError) throw customerError;
      setCustomer(customerData);

      // Fetch inquiry items
      const { data: itemsData, error: itemsError } = await supabase
        .from('inquiry_items')
        .select('*')
        .eq('inquiry_list_id', id)
        .order('sort_order', { ascending: true });

      if (itemsError) throw itemsError;
      setItems(itemsData || []);

    } catch (error) {
      console.error('Error fetching inquiry details:', error);
      showError('Failed to load inquiry details');
      const basePath = context === 'admin' ? '/admin' : '/staff-area';
      history.push(`${basePath}/inquiries`);
    } finally {
      setLoading(false);
    }
  };

  // Update item price
  const handleUpdateItemPrice = (itemId, price) => {
    setItems(items.map(item => 
      item.id === itemId 
        ? { ...item, quoted_price: price } 
        : item
    ));
  };

  // Update item staff notes
  const handleUpdateItemNotes = (itemId, notes) => {
    setItems(items.map(item => 
      item.id === itemId 
        ? { ...item, staff_notes: notes } 
        : item
    ));
  };

  // Calculate total
  const calculateTotal = () => {
    return items.reduce((sum, item) => {
      const price = parseFloat(item.quoted_price) || 0;
      return sum + (price * item.quantity);
    }, 0);
  };

  // Save draft quote
  const handleSaveDraft = async () => {
    try {
      setSaving(true);

      // Update inquiry list
      const { error: listError } = await supabase
        .from('inquiry_lists')
        .update({
          notes: staffNotes,
          quote_reference: quoteReference || null,
          status: 'under_review'
        })
        .eq('id', id);

      if (listError) throw listError;

      // Update items
      for (const item of items) {
        const { error: itemError } = await supabase
          .from('inquiry_items')
          .update({
            quoted_price: item.quoted_price || null,
            staff_notes: item.staff_notes || null
          })
          .eq('id', item.id);

        if (itemError) throw itemError;
      }

      showNotification('Quote draft saved successfully');
    } catch (error) {
      console.error('Error saving draft:', error);
      showError('Failed to save draft: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // Send quote to customer
  const handleSendQuote = async () => {
    // Validate all items have prices
    const itemsWithoutPrices = items.filter(item => !item.quoted_price || parseFloat(item.quoted_price) <= 0);
    
    if (itemsWithoutPrices.length > 0) {
      showError(`Please provide quoted prices for all items (${itemsWithoutPrices.length} items missing prices)`);
      return;
    }

    if (!window.confirm('Send this quote to the customer? They will be notified via email.')) {
      return;
    }

    try {
      setSaving(true);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();

      // Prepare items with prices
      const itemsWithPrices = items.map(item => ({
        item_id: item.id,
        quoted_price: parseFloat(item.quoted_price),
        staff_notes: item.staff_notes || null
      }));

      // Call update_inquiry_quote function
      const { error: quoteError } = await supabase
        .rpc('update_inquiry_quote', {
          inquiry_list_id: id,
          staff_uuid: user.id,
          items_with_prices: itemsWithPrices,
          notes: staffNotes || null
        });

      if (quoteError) throw quoteError;

      // Update quote reference if provided
      if (quoteReference) {
        await supabase
          .from('inquiry_lists')
          .update({ quote_reference: quoteReference })
          .eq('id', id);
      }

      // Send quote notification to customer
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const response = await fetch(
          `${supabase.supabaseUrl}/functions/v1/send-inquiry-quote`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session?.access_token}`,
            },
            body: JSON.stringify({ inquiryListId: id }),
          }
        );

        if (!response.ok) {
          console.error('Failed to send quote notification:', await response.text());
        }
      } catch (notifError) {
        console.error('Error sending quote notification:', notifError);
        // Don't fail the quote if notification fails
      }

      showNotification('Quote sent successfully to customer!');
      const basePath = context === 'admin' ? '/admin' : '/staff-area';
      history.push(`${basePath}/inquiries`);
    } catch (error) {
      console.error('Error sending quote:', error);
      showError('Failed to send quote: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // Archive inquiry
  const handleArchiveInquiry = async () => {
    if (!window.confirm('Archive this inquiry? It will be hidden from the active inquiry list.')) {
      return;
    }

    try {
      setSaving(true);

      // Call the archive_inquiry function
      const { error } = await supabase
        .rpc('archive_inquiry', {
          inquiry_list_id: id
        });

      if (error) throw error;

      showNotification('Inquiry archived successfully');
      const basePath = context === 'admin' ? '/admin' : '/staff-area';
      history.push(`${basePath}/inquiries`);
    } catch (error) {
      console.error('Error archiving inquiry:', error);
      showError('Failed to archive inquiry: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!inquiry) {
    return null;
  }

  const totalQuote = calculateTotal();
  const allItemsPriced = items.every(item => item.quoted_price && parseFloat(item.quoted_price) > 0);

  return (
    <div>
      {/* Header */}
      <PageHeader
        title={inquiry.list_name || 'Inquiry'}
        description={inquiry.description}
        meta={inquiry.is_archived && (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-200 text-gray-700">
            <Archive size={16} className="mr-1" />
            This inquiry is archived
          </span>
        )}
        actions={
          <Button
            variant="secondary"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => {
              const basePath = context === 'admin' ? '/admin' : '/staff-area';
              history.push(`${basePath}/inquiries`);
            }}
          >
            Back to Inquiries
          </Button>
        }
        className="mb-6"
      />

      {/* Customer & Inquiry Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Customer Info */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <User size={20} className="text-gray-500" />
            Customer Information
          </h2>
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-gray-600">Name:</span>
              <span className="ml-2 font-medium">{customer?.full_name}</span>
            </div>
            {customer?.company_name && (
              <div>
                <span className="text-gray-600">Company:</span>
                <span className="ml-2 font-medium">{customer.company_name}</span>
              </div>
            )}
            <div>
              <span className="text-gray-600">Email:</span>
              <span className="ml-2 font-medium">{customer?.email}</span>
            </div>
          </div>
        </div>

        {/* Inquiry Info */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Calendar size={20} className="text-gray-500" />
            Inquiry Details
          </h2>
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-gray-600">Submitted:</span>
              <span className="ml-2 font-medium">{formatDate(inquiry.submitted_at)}</span>
            </div>
            <div>
              <span className="text-gray-600">Status:</span>
              <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                inquiry.status === 'submitted' ? 'bg-blue-100 text-blue-800' :
                inquiry.status === 'under_review' ? 'bg-yellow-100 text-yellow-800' :
                'bg-green-100 text-green-800'
              }`}>
                {inquiry.status.charAt(0).toUpperCase() + inquiry.status.slice(1).replace('_', ' ')}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Total Items:</span>
              <span className="ml-2 font-medium">{items.length}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Quote Reference & Notes */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-base font-semibold text-gray-900 mb-4">Quote Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quote Reference (Optional)
            </label>
            <input
              type="text"
              value={quoteReference}
              onChange={(e) => setQuoteReference(e.target.value)}
              placeholder="e.g., Q-2025-001"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Internal Notes
          </label>
          <textarea
            value={staffNotes}
            onChange={(e) => setStaffNotes(e.target.value)}
            placeholder="Add internal notes about this quote..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          />
        </div>
      </div>

      {/* Items List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <Package size={20} className="text-gray-500" />
            Items to Quote
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Part Number</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Qty</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer Notes</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Subtotal</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {items.map((item, index) => {
                const subtotal = (parseFloat(item.quoted_price) || 0) * item.quantity;
                return (
                  <tr key={item.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{item.part_number}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{item.description}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 text-center">{item.quantity}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {item.customer_notes || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-right">
                      <div className="flex items-center justify-end gap-2">
                        <DollarSign size={16} className="text-gray-400" />
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.quoted_price || ''}
                          onChange={(e) => handleUpdateItemPrice(item.id, e.target.value)}
                          placeholder="0.00"
                          className="w-24 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-right"
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 text-right">
                      ${subtotal.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
              <tr className="bg-gray-100 font-semibold">
                <td colSpan="5" className="px-6 py-4 text-sm text-right">Total Quote:</td>
                <td className="px-6 py-4 text-sm text-right text-green-600 font-bold text-lg">
                  ${totalQuote.toFixed(2)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 justify-between">
        {/* Left side - Archive button */}
        <button
          onClick={handleArchiveInquiry}
          disabled={saving}
          className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          <Archive size={20} />
          Archive Inquiry
        </button>

        {/* Right side - Save and Send buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleSaveDraft}
            disabled={saving}
            className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <Save size={20} />
            Save Draft
          </button>
          <button
            onClick={handleSendQuote}
            disabled={saving || !allItemsPriced}
            className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send size={20} />
            {saving ? 'Sending...' : 'Send Quote to Customer'}
          </button>
        </div>
      </div>

      {!allItemsPriced && (
        <p className="text-sm text-red-600 text-right mt-2">
          * All items must have quoted prices before sending to customer
        </p>
      )}
    </div>
  );
};

export default InquiryReview;
